import { useState } from 'react'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import { ContributorNameDocument } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { getCurrencySymbolFromCode } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import EmployeeSidepanel from 'modules/project_management/components/EmployeeSidepanel'

type Props = {
  avatarURL: string
  billRate: number
  canManageRate: boolean
  currency: string
  employeeLastName: string
  employeeName: string
  costRate: number
  profitMargin: number
  employeeId: number
  jobTitle: string
}

const ContributorHeader = ({
  avatarURL,
  billRate,
  canManageRate,
  currency,
  employeeLastName,
  employeeName,
  costRate,
  profitMargin,
  employeeId,
  jobTitle,
}: Props) => {
  const [showEmployeeSidepanel, setShowEmployeeSidepanel] = useState(false)
  const selectedEmployeeInfo = {
    avatar: avatarURL,
    id: employeeId,
    firstName: employeeName,
    lastName: employeeLastName,
    billingRateCents: billRate,
    costRate,
    profitabilityPercentageCents: profitMargin,
    currency,
  }
  const costText = !!costRate
    ? {
        type: 'text' as const,
        content: (costRate / 100).toString() + getCurrencySymbolFromCode(currency),
      }
    : {
        type: 'status' as const,
        label: i18n.t('project_management.project_workers.details.cost_missing.title'),
        variant: 'info' as const,
      }
  return (
    <>
      <ResourceHeader
        title={`${employeeName} ${employeeLastName}`}
        avatar={{
          src: avatarURL || undefined,
          firstName: employeeName,
          lastName: employeeLastName,
          type: 'person',
        }}
        metadata={[
          {
            label: i18n.t('project_management.headers.seniority'),
            value: {
              type: 'text',
              content: jobTitle,
            },
          },
          {
            label: i18n.t('project_management.headers.max_cost_rate'),
            value: costText,
            info: !!costRate
              ? undefined
              : {
                  title: i18n.t(
                    'project_management.project_workers.details.cost_missing.tooltip_message'
                  ),
                },
          },
          {
            label: i18n.t('project_management.headers.bill_rate'),
            value: {
              type: 'text',
              content: (billRate / 100).toString() + getCurrencySymbolFromCode(currency),
            },
          },
          {
            label: i18n.t('project_management.headers.min_profit_margin'),
            value: {
              type: 'text',
              content: profitMargin / 100 + '%',
            },
          },
        ]}
        primaryAction={{
          label: i18n.t('project_management.people_and_rates.sidepanel.title.edit_rates'),
          icon: Pencil,
          isVisible: canManageRate,
          onClick: () => {
            track(trackingEvents.projectManagement.jobsAndRates.clickOnEditEmployeeRate, {
              origin: 'from_people_details_view',
            })
            setShowEmployeeSidepanel(true)
          },
        }}
      />
      <EmployeeSidepanel
        showSidePanel={showEmployeeSidepanel}
        onClose={() => setShowEmployeeSidepanel(false)}
        refetchQueries={[ContributorNameDocument]}
        selectedEmployeeInfo={selectedEmployeeInfo}
      />
    </>
  )
}

export default ContributorHeader
